"use client"

/**
 * Day-grid date picker. Hand-rolled rather than built on `react-day-picker`:
 * that engine is already a dependency of `ui-blocks` and the storybook app,
 * but never of `ui-core`, and `packages/ui-core/package.json` is outside this
 * component's owned files — declaring the dependency there was not this
 * change's to make. See the v2 README for the primitive/block split; a block
 * that already owns its own `package.json` edit is free to reconsider this.
 */

import { ChevronLeft, ChevronRight } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react"
import { DURATION_BASE, EASE_OUT } from "../../lib/ease"
import { cn } from "../../lib/utils"
import { IconButton } from "./icon-button"

export type CalendarMode = "single" | "multiple" | "range"
export type CalendarSize = "sm" | "md" | "lg"

export interface DateRange {
  from: Date
  to?: Date
}

/**
 * Matches a date against the disabled set: a predicate, an explicit list, or
 * an (open-ended) range.
 */
export type CalendarDisabledMatcher = ((date: Date) => boolean) | Date[] | { from?: Date; to?: Date }

/**
 * A quick-pick rail entry. `value` is loosely typed across all three shapes
 * rather than threaded through a `Calendar<TMode>` generic — the calendar's
 * own props are already a discriminated union on `mode`, and a caller wires a
 * preset list that matches whichever mode it renders under. Flagged in the
 * build report: tighten to a mode-conditional generic if a consuming block
 * needs the stricter type. Until then, `handlePresetClick` runtime-checks
 * `value` against the rendered `mode` and skips (with a dev-only
 * `console.warn`) a preset whose shape doesn't match, rather than letting a
 * mismatched value reach `onSelect`.
 */
export interface CalendarPreset {
  label: string
  value: Date | Date[] | DateRange
}

interface CalendarBaseProps {
  /** Month shown on first render. Defaults to the selected date, or today. */
  defaultMonth?: Date
  /** How many months to render side by side. Defaults to 1; use 2 for a range spread. */
  numberOfMonths?: number
  disabled?: CalendarDisabledMatcher
  min?: Date
  max?: Date
  /** First column of the week. Defaults to 1 (Monday), matching this repo's other date displays. */
  weekStartsOn?: 0 | 1
  size?: CalendarSize
  /** Render the leading/trailing days that fill out the grid. Defaults to true. */
  showOutsideDays?: boolean
  showWeekNumbers?: boolean
  /** Quick-pick rail rendered beside the grid. */
  presets?: CalendarPreset[]
  className?: string
}

export type CalendarProps = CalendarBaseProps &
  (
    | { mode: "single"; selected?: Date; onSelect?: (date: Date | undefined) => void }
    | { mode: "multiple"; selected?: Date[]; onSelect?: (dates: Date[] | undefined) => void }
    | { mode: "range"; selected?: DateRange; onSelect?: (range: DateRange | undefined) => void }
  )

/* ---------------------------------------------------------------------- */
/* Date helpers — plain `Date` math. Neither `date-fns` nor                */
/* `react-day-picker` is a declared `ui-core` dependency (see file header), */
/* so this primitive stays dependency-free rather than reaching for either. */
/* ---------------------------------------------------------------------- */

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function isSameDay(a: Date | undefined, b: Date | undefined): boolean {
  if (!a || !b) return false
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

function addDays(date: Date, amount: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + amount)
  return next
}

function addMonths(date: Date, amount: number): Date {
  const next = new Date(date)
  const day = next.getDate()
  next.setDate(1)
  next.setMonth(next.getMonth() + amount)
  const daysInTarget = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
  next.setDate(Math.min(day, daysInTarget))
  return next
}

function addYears(date: Date, amount: number): Date {
  return addMonths(date, amount * 12)
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function isBefore(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() < startOfDay(b).getTime()
}

function isAfter(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() > startOfDay(b).getTime()
}

function clampDate(date: Date, min?: Date, max?: Date): Date {
  if (min && isBefore(date, min)) return startOfDay(min)
  if (max && isAfter(date, max)) return startOfDay(max)
  return date
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}

/** Six full weeks (42 cells), so the grid height never shifts month to month. */
function buildMonthGrid(month: Date, weekStartsOn: 0 | 1): Date[][] {
  const first = startOfMonth(month)
  const offset = (first.getDay() - weekStartsOn + 7) % 7
  const gridStart = addDays(first, -offset)
  const weeks: Date[][] = []
  let cursor = gridStart
  for (let week = 0; week < 6; week += 1) {
    const days: Date[] = []
    for (let day = 0; day < 7; day += 1) {
      days.push(cursor)
      cursor = addDays(cursor, 1)
    }
    weeks.push(days)
  }
  return weeks
}

function isDateDisabled(date: Date, disabled: CalendarDisabledMatcher | undefined, min?: Date, max?: Date): boolean {
  if (min && isBefore(date, min)) return true
  if (max && isAfter(date, max)) return true
  if (!disabled) return false
  if (typeof disabled === "function") return disabled(date)
  if (Array.isArray(disabled)) return disabled.some((candidate) => isSameDay(candidate, date))
  const { from, to } = disabled
  if (from && to) return !isBefore(date, from) && !isAfter(date, to)
  if (from) return !isBefore(date, from)
  if (to) return !isAfter(date, to)
  return false
}

/** ISO-8601 week number (Monday-based, week 1 contains the year's first Thursday). */
function getISOWeekNumber(date: Date): number {
  const target = startOfDay(date)
  const dayNumber = (target.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNumber + 3)
  const firstThursday = new Date(target.getFullYear(), 0, 4)
  const firstDayNumber = (firstThursday.getDay() + 6) % 7
  firstThursday.setDate(firstThursday.getDate() - firstDayNumber + 3)
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000))
}

function getWeekdayLabels(weekStartsOn: 0 | 1): { key: string; narrow: string; full: string }[] {
  const reference = new Date(2023, 0, 1 + weekStartsOn)
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(reference, index)
    return {
      key: dateKey(date),
      narrow: new Intl.DateTimeFormat(undefined, { weekday: "narrow" }).format(date),
      full: new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(date),
    }
  })
}

function formatMonthCaption(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date)
}

function formatFullDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

/* ---------------------------------------------------------------------- */
/* Selection resolution — one function per mode, sharing the same shape.   */
/* ---------------------------------------------------------------------- */

function resolveSingleSelection(day: Date, selected: Date | undefined): Date | undefined {
  return isSameDay(day, selected) ? undefined : day
}

function resolveMultipleSelection(day: Date, selected: Date[] | undefined): Date[] {
  const current = selected ?? []
  const index = current.findIndex((candidate) => isSameDay(candidate, day))
  if (index === -1) return [...current, day]
  return [...current.slice(0, index), ...current.slice(index + 1)]
}

function resolveRangeSelection(day: Date, selected: DateRange | undefined): DateRange {
  if (!selected || (selected.from && selected.to)) return { from: day, to: undefined }
  if (isBefore(day, selected.from)) return { from: day, to: selected.from }
  if (isSameDay(day, selected.from)) return { from: day, to: undefined }
  return { from: selected.from, to: day }
}

/* ---------------------------------------------------------------------- */
/* Sizing ladder — mirrors the v2 button/icon-button steps.                */
/* ---------------------------------------------------------------------- */

const SIZE_CONFIG: Record<
  CalendarSize,
  { cell: string; weekNumber: string; text: string; caption: string; nav: "sm" | "md"; gap: string; rowHeightPx: number }
> = {
  sm: { cell: "size-8", weekNumber: "w-8", text: "text-v2-xs", caption: "text-v2-sm", nav: "sm", gap: "gap-1", rowHeightPx: 32 },
  md: { cell: "size-9", weekNumber: "w-9", text: "text-v2-sm", caption: "text-v2-base", nav: "sm", gap: "gap-1", rowHeightPx: 36 },
  lg: { cell: "size-10", weekNumber: "w-10", text: "text-v2-base", caption: "text-v2-base", nav: "md", gap: "gap-1.5", rowHeightPx: 40 },
}

/**
 * Grid height reserved so the month-transition overlap (previous month
 * fading out while the next fades in, both `absolute`) never collapses the
 * panel to zero height mid-animation. Six rows at the size's cell height,
 * plus five row gaps at the size's gap width.
 */
function gridHeightPx(size: CalendarSize): number {
  const { rowHeightPx } = SIZE_CONFIG[size]
  const gapPx = size === "lg" ? 6 : 4
  return rowHeightPx * 6 + gapPx * 5
}

/**
 * Month-slide timing: `DURATION_BASE` / `EASE_OUT` from `lib/ease.ts`, the
 * same duration-and-easing pair `Popover` and `Button` animate on — never an
 * inline duration or a curve invented for this component alone. The blur and
 * travel distance below are not part of the duration/easing scale; they are
 * this transition's own small, fixed geometry.
 */
const MONTH_TRANSITION_BLUR_PX = 3
const MONTH_TRANSITION_DISTANCE_PX = 8

interface DayState {
  date: Date
  outside: boolean
  disabled: boolean
  today: boolean
  selected: boolean
  rangeStart: boolean
  rangeEnd: boolean
  inRange: boolean
  previewInRange: boolean
}

export function Calendar(props: CalendarProps) {
  const {
    mode,
    selected,
    onSelect,
    defaultMonth,
    numberOfMonths = 1,
    disabled,
    min,
    max,
    weekStartsOn = 1,
    size = "md",
    showOutsideDays = true,
    showWeekNumbers = false,
    presets,
    className,
  } = props

  // Across two or more panels a month's trailing days are the next panel's
  // leading days, so an outside day duplicates the neighbouring panel's own
  // cell (e.g. Aug 31 shows in both August and September) — confusing to select
  // against. Suppress outside days whenever more than one month is on screen;
  // a lone month still shows them for a full six-week grid.
  const resolvedShowOutsideDays = showOutsideDays && numberOfMonths <= 1

  const reduceMotion = useReducedMotion()
  const today = useMemo(() => startOfDay(new Date()), [])

  const initialAnchor = useMemo(() => {
    if (defaultMonth) return startOfMonth(defaultMonth)
    if (mode === "single" && selected) return startOfMonth(selected)
    if (mode === "range" && selected?.from) return startOfMonth(selected.from)
    const [firstSelected] = mode === "multiple" ? (selected ?? []) : []
    if (firstSelected) return startOfMonth(firstSelected)
    return startOfMonth(today)
    // Only ever consulted on mount — `defaultMonth` is an initializer, not a controlled value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [anchorMonth, setAnchorMonth] = useState(initialAnchor)
  const [focusedDate, setFocusedDate] = useState(() => clampDate(initialAnchor, min, max))
  const [direction, setDirection] = useState(0)
  const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined)
  const [announcement, setAnnouncement] = useState("")

  const dayRefs = useRef(new Map<string, HTMLButtonElement>())
  const previousSelectedKey = useRef<string>("")
  const captionId = useId()
  const liveRegionId = useId()

  const weekdayLabels = useMemo(() => getWeekdayLabels(weekStartsOn), [weekStartsOn])

  const visibleMonths = useMemo(
    () => Array.from({ length: numberOfMonths }, (_, index) => addMonths(anchorMonth, index)),
    [anchorMonth, numberOfMonths],
  )

  const navigateToMonth = useCallback(
    (nextAnchor: Date, nextFocused: Date) => {
      const clampedFocused = clampDate(nextFocused, min, max)
      setDirection(nextAnchor.getTime() === anchorMonth.getTime() ? 0 : nextAnchor > anchorMonth ? 1 : -1)
      setAnchorMonth(startOfMonth(nextAnchor))
      setFocusedDate(clampedFocused)
    },
    [anchorMonth, min, max],
  )

  const goToAdjacentMonth = useCallback(
    (step: number) => {
      const nextFocused = addMonths(focusedDate, step)
      navigateToMonth(addMonths(anchorMonth, step), nextFocused)
    },
    [anchorMonth, focusedDate, navigateToMonth],
  )

  /** Brings the anchor back into view when keyboard focus moves outside the visible window. */
  const moveFocus = useCallback(
    (nextFocused: Date) => {
      const clamped = clampDate(nextFocused, min, max)
      const firstVisible = anchorMonth
      const lastVisible = addMonths(anchorMonth, numberOfMonths - 1)
      if (isBefore(clamped, startOfMonth(firstVisible))) {
        navigateToMonth(startOfMonth(clamped), clamped)
      } else if (!isBefore(clamped, addMonths(startOfMonth(lastVisible), 1))) {
        navigateToMonth(addMonths(startOfMonth(clamped), -(numberOfMonths - 1)), clamped)
      } else {
        setFocusedDate(clamped)
      }
    },
    [anchorMonth, numberOfMonths, min, max, navigateToMonth],
  )

  /**
   * Roving tabindex: move the DOM focus to whichever cell becomes the `0` in
   * the ladder, but only in response to a change after mount — a fresh
   * `Calendar` must not steal focus the instant it renders (several can be on
   * screen at once, as in this file's own Storybook stories), only once the
   * grid already has it and a key moves it.
   */
  const hasMountedRef = useRef(false)
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    const button = dayRefs.current.get(dateKey(focusedDate))
    if (button && document.activeElement !== button) button.focus()
  }, [focusedDate, anchorMonth])

  const commitSelection = useCallback(
    (day: Date) => {
      if (mode === "single") {
        const next = resolveSingleSelection(day, selected)
        onSelect?.(next)
      } else if (mode === "multiple") {
        const next = resolveMultipleSelection(day, selected)
        onSelect?.(next)
      } else {
        const next = resolveRangeSelection(day, selected)
        onSelect?.(next)
      }
    },
    [mode, selected, onSelect],
  )

  const handleDaySelect = useCallback(
    (day: Date) => {
      if (isDateDisabled(day, disabled, min, max)) return
      commitSelection(day)
      setFocusedDate(day)
    },
    [disabled, min, max, commitSelection],
  )

  const handleGridKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const key = event.key
      const stepMap = {
        ArrowLeft: -1,
        ArrowRight: 1,
        ArrowUp: -7,
        ArrowDown: 7,
      } as const

      if (key === "ArrowLeft" || key === "ArrowRight" || key === "ArrowUp" || key === "ArrowDown") {
        event.preventDefault()
        moveFocus(addDays(focusedDate, stepMap[key]))
        return
      }

      if (key === "Home" || key === "End") {
        event.preventDefault()
        const weekday = (focusedDate.getDay() - weekStartsOn + 7) % 7
        moveFocus(addDays(focusedDate, key === "Home" ? -weekday : 6 - weekday))
        return
      }

      if (key === "PageUp") {
        event.preventDefault()
        if (event.shiftKey) moveFocus(addYears(focusedDate, -1))
        else goToAdjacentMonth(-1)
        return
      }

      if (key === "PageDown") {
        event.preventDefault()
        if (event.shiftKey) moveFocus(addYears(focusedDate, 1))
        else goToAdjacentMonth(1)
        return
      }

      if (key === "Enter" || key === " ") {
        event.preventDefault()
        handleDaySelect(focusedDate)
      }
    },
    [focusedDate, weekStartsOn, moveFocus, goToAdjacentMonth, handleDaySelect],
  )

  useEffect(() => {
    const selectionKey =
      mode === "single"
        ? (selected ? dateKey(selected) : "")
        : mode === "multiple"
          ? (selected ?? []).map(dateKey).join(",")
          : `${selected?.from ? dateKey(selected.from) : ""}:${selected?.to ? dateKey(selected.to) : ""}`

    if (selectionKey === previousSelectedKey.current) return
    previousSelectedKey.current = selectionKey

    if (!selectionKey) {
      setAnnouncement("Selection cleared")
    } else if (mode === "single" && selected) {
      setAnnouncement(`Selected ${formatFullDate(selected)}`)
    } else if (mode === "multiple" && selected?.length) {
      setAnnouncement(`Selected ${selected.length} date${selected.length === 1 ? "" : "s"}`)
    } else if (mode === "range" && selected?.from) {
      setAnnouncement(
        selected.to
          ? `Selected ${formatFullDate(selected.from)} to ${formatFullDate(selected.to)}`
          : `Range start ${formatFullDate(selected.from)}`,
      )
    }
    // Only the serialized selection should retrigger this — see selectionKey above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selected])

  const sizeConfig = SIZE_CONFIG[size]

  /**
   * Variant functions, not plain objects: `AnimatePresence` re-invokes `exit`
   * with its own live `custom` at the moment a child actually leaves, which is
   * what lets the outgoing month read the *current* `direction` rather than
   * whatever `direction` was when that child last rendered as the incoming
   * one. A plain object bakes in the stale value and slides the wrong way
   * whenever navigation reverses (next, then prev).
   */
  const slideVariants = {
    enter: (dir: number) =>
      reduceMotion
        ? { opacity: 0 }
        : { opacity: 0, x: dir * MONTH_TRANSITION_DISTANCE_PX, filter: `blur(${MONTH_TRANSITION_BLUR_PX}px)` },
    center: () => (reduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, filter: "blur(0px)" }),
    exit: (dir: number) =>
      reduceMotion
        ? { opacity: 0 }
        : { opacity: 0, x: -dir * MONTH_TRANSITION_DISTANCE_PX, filter: `blur(${MONTH_TRANSITION_BLUR_PX}px)` },
  }

  const buildDayStates = useCallback(
    (month: Date): DayState[][] => {
      const weeks = buildMonthGrid(month, weekStartsOn)
      const rangeFrom = mode === "range" ? selected?.from : undefined
      const rangeTo = mode === "range" ? selected?.to : undefined
      const previewTo = mode === "range" && rangeFrom && !rangeTo ? hoverDate : undefined

      return weeks.map((week) =>
        week.map((date) => {
          const outside = !isSameMonth(date, month)
          const dayDisabled = isDateDisabled(date, disabled, min, max)
          const isToday = isSameDay(date, today)

          let selectedFlag = false
          let rangeStart = false
          let rangeEnd = false
          let inRange = false
          let previewInRange = false

          if (mode === "single") {
            selectedFlag = isSameDay(date, selected)
          } else if (mode === "multiple") {
            selectedFlag = (selected ?? []).some((candidate) => isSameDay(candidate, date))
          } else if (rangeFrom) {
            rangeStart = isSameDay(date, rangeFrom)
            rangeEnd = rangeTo ? isSameDay(date, rangeTo) : false
            selectedFlag = rangeStart || rangeEnd
            if (rangeTo) {
              inRange = isAfter(date, rangeFrom) && isBefore(date, rangeTo)
            } else if (previewTo) {
              const lo = isBefore(previewTo, rangeFrom) ? previewTo : rangeFrom
              const hi = isBefore(previewTo, rangeFrom) ? rangeFrom : previewTo
              previewInRange = isAfter(date, lo) && isBefore(date, hi)
            }
          }

          return {
            date,
            outside,
            disabled: dayDisabled,
            today: isToday,
            selected: selectedFlag,
            rangeStart,
            rangeEnd,
            inRange,
            previewInRange,
          }
        }),
      )
    },
    [mode, selected, disabled, min, max, today, weekStartsOn, hoverDate],
  )

  const handlePresetClick = (preset: CalendarPreset) => {
    const { value } = preset
    const matchesMode =
      mode === "single"
        ? value instanceof Date
        : mode === "multiple"
          ? Array.isArray(value)
          : !(value instanceof Date) && !Array.isArray(value)

    if (!matchesMode) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `Calendar: preset "${preset.label}" provides a value that doesn't match mode="${mode}"; ignoring the click.`,
        )
      }
      return
    }

    ;(onSelect as ((value: Date | Date[] | DateRange) => void) | undefined)?.(value)
    const jumpTo = value instanceof Date ? value : Array.isArray(value) ? value[0] : value.from
    if (jumpTo) navigateToMonth(startOfMonth(jumpTo), jumpTo)
  }

  return (
    <div className={cn("inline-flex items-start gap-4", className)}>
      {presets && presets.length > 0 ? (
        <div className="flex w-36 shrink-0 flex-col gap-0.5 border-r border-v2-border pr-3" role="group" aria-label="Presets">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => handlePresetClick(preset)}
              className={cn(
                "rounded-v2-lg px-2 py-1.5 text-left text-v2-sm text-v2-foreground transition-colors",
                "hover:bg-v2-accent hover:text-v2-accent-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2 ring-offset-v2-background",
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex gap-4">
        {visibleMonths.map((month, panelIndex) => {
          const weeks = buildDayStates(month)
          const panelCaptionId = `${captionId}-${panelIndex}`
          const monthLabel = formatMonthCaption(month)

          return (
            // Stable per-slot key: the *panel* must not remount when its month
            // changes, or the `AnimatePresence` below is torn down before its
            // exit animation can run. `monthKey(month)` is the inner grid's key
            // instead, which is what tells `AnimatePresence` to swap children.
            <div key={`panel-${panelIndex}`} className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                {panelIndex === 0 ? (
                  <IconButton
                    size={sizeConfig.nav}
                    aria-label={`Go to ${formatMonthCaption(addMonths(month, -1))}`}
                    onClick={() => goToAdjacentMonth(-1)}
                  >
                    <ChevronLeft className="size-4" />
                  </IconButton>
                ) : (
                  <span aria-hidden className={cn(sizeConfig.nav === "md" ? "size-9" : "size-8")} />
                )}

                <h2 id={panelCaptionId} className={cn("font-medium text-v2-foreground", sizeConfig.caption)}>
                  {monthLabel}
                </h2>

                {panelIndex === numberOfMonths - 1 ? (
                  <IconButton
                    size={sizeConfig.nav}
                    aria-label={`Go to ${formatMonthCaption(addMonths(month, 1))}`}
                    onClick={() => goToAdjacentMonth(1)}
                  >
                    <ChevronRight className="size-4" />
                  </IconButton>
                ) : (
                  <span aria-hidden className={cn(sizeConfig.nav === "md" ? "size-9" : "size-8")} />
                )}
              </div>

              <div
                role="grid"
                aria-labelledby={panelCaptionId}
                className="flex flex-col gap-3"
                onKeyDown={handleGridKeyDown}
              >
                <div role="row" className={cn("flex items-center", sizeConfig.gap)}>
                  {showWeekNumbers ? <span className={cn(sizeConfig.weekNumber, "shrink-0")} aria-hidden /> : null}
                  {weekdayLabels.map((weekday) => (
                    <span
                      key={weekday.key}
                      role="columnheader"
                      aria-label={weekday.full}
                      className={cn(
                        sizeConfig.cell,
                        sizeConfig.text,
                        "flex shrink-0 items-center justify-center font-medium text-v2-muted-foreground",
                      )}
                    >
                      {weekday.narrow}
                    </span>
                  ))}
                </div>

                <div className="relative" style={{ height: gridHeightPx(size) }}>
                  <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.div
                      key={monthKey(month)}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: reduceMotion ? 0.1 : DURATION_BASE, ease: EASE_OUT }}
                      className={cn("absolute inset-0 flex flex-col", sizeConfig.gap)}
                    >
                      {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} role="row" className={cn("flex items-center", sizeConfig.gap)}>
                          {showWeekNumbers ? (
                            <span
                              role="rowheader"
                              className={cn(
                                sizeConfig.weekNumber,
                                sizeConfig.cell.replace("size-", "h-"),
                                sizeConfig.text,
                                "flex shrink-0 items-center justify-center tabular-nums text-v2-muted-foreground",
                              )}
                            >
                              {/* `week` always has exactly 7 entries — see `buildMonthGrid`. */}
                              {getISOWeekNumber((week[3] ?? week[0])!.date)}
                            </span>
                          ) : null}
                          {week.map((day) => {
                            if (day.outside && !resolvedShowOutsideDays) {
                              return (
                                <span key={dateKey(day.date)} aria-hidden className={cn(sizeConfig.cell, "shrink-0")} />
                              )
                            }

                            const isFocusable = isSameDay(day.date, focusedDate) && !day.outside

                            return (
                              <button
                                key={dateKey(day.date)}
                                ref={(node) => {
                                  // Outside cells are visual filler for a neighboring panel's
                                  // month, not that date's authoritative cell — never register
                                  // them, or two panels can register the same `dateKey` and
                                  // whichever mounts last wins the roving-focus target.
                                  const key = dateKey(day.date)
                                  if (node && !day.outside) dayRefs.current.set(key, node)
                                  else dayRefs.current.delete(key)
                                }}
                                type="button"
                                role="gridcell"
                                tabIndex={isFocusable ? 0 : -1}
                                aria-selected={day.selected}
                                aria-current={day.today ? "date" : undefined}
                                aria-disabled={day.disabled || undefined}
                                aria-label={formatFullDate(day.date)}
                                disabled={day.disabled}
                                onFocus={() => setFocusedDate(day.date)}
                                onClick={() => handleDaySelect(day.date)}
                                onMouseEnter={() => mode === "range" && setHoverDate(day.date)}
                                onMouseLeave={() => mode === "range" && setHoverDate(undefined)}
                                className={cn(
                                  sizeConfig.cell,
                                  sizeConfig.text,
                                  // `rounded-v2-lg` lives here, on every cell, not only the
                                  // selected branch below — the hover fill and the selected
                                  // pill must share one radius. It previously sat only on the
                                  // `day.selected` class, so an unselected cell's `hover:bg-*`
                                  // painted square corners.
                                  "relative flex shrink-0 items-center justify-center rounded-v2-lg font-medium tabular-nums transition-colors",
                                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2 ring-offset-v2-background",
                                  "disabled:pointer-events-none disabled:opacity-40",
                                  day.outside && "text-v2-muted-foreground/60",
                                  !day.outside && !day.selected && "text-v2-foreground",
                                  !day.selected && !day.inRange && !day.previewInRange && "hover:bg-v2-accent hover:text-v2-accent-foreground",
                                  (day.inRange || day.previewInRange) && !day.selected && "bg-v2-primary/10 rounded-none",
                                  day.selected && "bg-v2-primary text-v2-primary-foreground",
                                  day.rangeStart && !day.rangeEnd && "rounded-r-none",
                                  day.rangeEnd && !day.rangeStart && "rounded-l-none",
                                )}
                              >
                                {day.date.getDate()}
                                {day.today && !day.selected ? (
                                  <span
                                    aria-hidden
                                    className="absolute bottom-1 size-1 rounded-full bg-v2-primary"
                                  />
                                ) : null}
                              </button>
                            )
                          })}
                        </div>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <span id={liveRegionId} role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  )
}

Calendar.displayName = "Calendar"
